import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';

export function AccessTab({ memorialId, onChange }) {
  const [reqs, setReqs] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [tributes, setTributes] = useState([]);
  const [approved, setApproved] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([
      api.get(`/memorials/${memorialId}/access-requests`),
      api.get(`/memorials/${memorialId}/access-tokens`),
      api.get(`/memorials/${memorialId}/tributes`),
    ]);
    setReqs(a.data.requests); setTokens(b.data.tokens); setTributes(c.data.tributes);
  }, [memorialId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (req_id, approve) => {
    try {
      const { data } = await api.post(`/access-requests/${req_id}/decision`, { approve });
      if (approve) setApproved(data);
      load(); onChange?.();
    } catch (e) { alert(formatApiError(e)); }
  };
  const revoke = async (tid) => { await api.post(`/access-tokens/${tid}/revoke`); load(); };

  const decideTribute = async (tribute_id, approve) => {
    try {
      await api.post(`/tributes/${tribute_id}/decision`, { approve });
      load();
    } catch (e) { alert(formatApiError(e)); }
  };
  const deleteTribute = async (tribute_id) => {
    if (!window.confirm('Delete this tribute permanently?')) return;
    try {
      await api.delete(`/tributes/${tribute_id}`);
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const replyTribute = async (t) => {
    const reply = window.prompt(
      'Reply as the family — this response is shown publicly beneath the tribute:',
      t.family_reply || '',
    );
    if (reply == null || !reply.trim()) return;
    try {
      await api.post(`/tributes/${t.tribute_id}/reply`, { reply: reply.trim() });
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Family Vault access requests</h2>

      {approved && (
        <div className="ml-card border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5" data-testid="approved-link-card">
          <div className="ml-overline">Approved — share this secure link (expires in 24h)</div>
          <div className="mt-2 font-mono text-sm break-all" data-testid="approved-access-url">
            {approved.access_url || (window.location.origin + approved.access_url_fragment)}
          </div>

          {/* Delivery status */}
          {approved.delivery && (
            <div className="mt-4 space-y-2 text-xs">
              {['email', 'sms'].map((ch) => {
                const d = approved.delivery[ch];
                if (!d) return null;
                const ok = d.delivered;
                const missing = d.reason === 'not_configured';
                return (
                  <div key={ch} className={`flex items-center gap-2 ${ok ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))]'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-[hsl(var(--secondary))]' : 'bg-[hsl(var(--accent))]'}`} />
                    <span className="uppercase tracking-widest font-semibold">{ch}</span>
                    {ok ? <span>sent to {ch === 'email' ? (approved.delivery.email_to || 'recipient') : 'recipient'}</span>
                        : missing ? <span className="italic">{ch === 'email' ? 'SendGrid' : 'Twilio'} not configured yet — copy the link and send manually.</span>
                        : <span>delivery failed — copy the link and send manually.</span>}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => navigator.clipboard.writeText(approved.access_url || (window.location.origin + approved.access_url_fragment))} className="ml-btn-outline" data-testid="copy-access-link-btn">Copy link</button>
            <button onClick={() => setApproved(null)} className="ml-btn-primary">Done</button>
          </div>
        </div>
      )}

      {reqs.length === 0 ? (
        <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]">No requests yet.</div>
      ) : (
        <div className="space-y-3">
          {reqs.map((r) => (
            <div key={r.request_id} className="ml-card flex flex-wrap justify-between gap-4" data-testid={`request-${r.request_id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-xl text-[hsl(var(--primary))]">{r.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{r.relationship} · {r.email || r.phone}</div>
                {r.message && <p className="text-sm mt-2 text-[hsl(var(--muted-foreground))] italic">"{r.message}"</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status === 'pending' ? (
                  <>
                    <button onClick={() => decide(r.request_id, true)} className="ml-btn-primary !py-2" data-testid={`approve-${r.request_id}`}>Approve</button>
                    <button onClick={() => decide(r.request_id, false)} className="ml-btn-outline !py-2" data-testid={`deny-${r.request_id}`}>Deny</button>
                  </>
                ) : (
                  <span className="ml-overline !text-[hsl(var(--accent))]">{r.status}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-8">Active access links</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">All approved tokens. Revoke any time.</p>
        <div className="mt-3 space-y-2">
          {tokens.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">None active.</div>}
          {tokens.map((t) => {
            const exp = new Date(t.expires_at);
            const expired = exp < new Date();
            return (
              <div key={t.token_id} className="ml-card flex items-center justify-between" data-testid={`token-${t.token_id}`}>
                <div>
                  <div className="text-sm font-medium">{t.granted_to_name} · {t.relationship}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    Expires {exp.toLocaleString()} {t.revoked ? '· REVOKED' : expired ? '· expired' : ''}
                  </div>
                </div>
                {!t.revoked && !expired && (
                  <button onClick={() => revoke(t.token_id)} className="ml-btn-outline !py-2 text-xs" data-testid={`revoke-${t.token_id}`}>Revoke</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-10">Visitor memories</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          Memories submitted from the public memorial. Approve to publish, or reject to hide.
        </p>
        <div className="mt-3 space-y-3" data-testid="tributes-list">
          {tributes.length === 0 ? (
            <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]">
              No memories submitted yet.
            </div>
          ) : (
            tributes.map((t) => (
              <div
                key={t.tribute_id}
                className="ml-card"
                data-testid={`tribute-${t.tribute_id}`}
              >
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-lg text-[hsl(var(--primary))]">
                      {t.name}
                      {t.relationship && (
                        <span className="text-sm text-[hsl(var(--muted-foreground))] font-light italic ml-2">
                          · {t.relationship}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.5)] mt-1">
                      {new Date(t.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] tracking-widest font-semibold uppercase px-2 py-1 rounded-full border ${
                      t.status === 'approved'
                        ? 'border-green-700/40 text-green-800 bg-green-50'
                        : t.status === 'rejected'
                        ? 'border-red-700/30 text-red-700 bg-red-50'
                        : 'border-[hsl(var(--accent))]/50 text-[hsl(var(--accent))]'
                    }`}
                  >
                    {t.status}
                  </span>
                </div>
                <p className="mt-3 text-sm text-[hsl(var(--primary)/0.85)] leading-relaxed font-light whitespace-pre-line">
                  {t.message}
                </p>
                {t.video_url && (
                  <div className="mt-3" data-testid={`tribute-video-${t.tribute_id}`}>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--accent))] mb-1.5">
                      Visitor video memory ({((t.video_size || 0) / (1024 * 1024)).toFixed(1)} MB)
                    </div>
                    <video
                      src={t.status === 'approved' ? t.video_url : `/api/admin/tributes/${t.tribute_id}/video`}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded bg-black max-w-md"
                      style={{ maxHeight: 320 }}
                    />
                  </div>
                )}
                {t.family_reply && (
                  <div className="mt-3 ml-3 pl-3 border-l-2 border-[hsl(var(--accent))]" data-testid={`tribute-reply-${t.tribute_id}`}>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--accent))]">Family response</div>
                    <p className="mt-1 text-sm text-[hsl(var(--primary)/0.85)] font-light whitespace-pre-line">{t.family_reply}</p>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {t.status !== 'approved' && (
                    <button
                      onClick={() => decideTribute(t.tribute_id, true)}
                      className="ml-btn-primary !py-2"
                      data-testid={`approve-tribute-${t.tribute_id}`}
                    >
                      Approve
                    </button>
                  )}
                  {t.status === 'approved' && (
                    <button
                      onClick={() => replyTribute(t)}
                      className="ml-btn-outline !py-2"
                      data-testid={`reply-tribute-${t.tribute_id}`}
                    >
                      {t.family_reply ? 'Edit family reply' : 'Reply as family'}
                    </button>
                  )}
                  {t.status !== 'rejected' && (
                    <button
                      onClick={() => decideTribute(t.tribute_id, false)}
                      className="ml-btn-outline !py-2"
                      data-testid={`reject-tribute-${t.tribute_id}`}
                    >
                      Reject
                    </button>
                  )}
                  <button
                    onClick={() => deleteTribute(t.tribute_id)}
                    className="ml-btn-link !text-red-600 !py-2"
                    data-testid={`delete-tribute-${t.tribute_id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
